<template>
  <div class="container">
    <h3 class="title">{{ $t('ec-line041.title') }}</h3>
    <p class="form-explanation">{{ $t('ec-line041.formExplanation') }}</p>
    <el-form ref="form" :model="form" class="apply-form" label-position="top" :rules="rules">
      <el-form-item :label="$t('common.memberInformation.companyName')" prop="companyName" required>
        <el-input
          id="company-name"
          v-model="form.companyName"
          :placeholder="$t('common.informationPlaceholder.companyName')"
          :maxlength="40"
        ></el-input>
      </el-form-item>

      <el-form-item :label="$t('common.memberInformation.storeName')">
        <el-input
          id="store-name"
          v-model="form.storeName"
          :placeholder="$t('common.informationPlaceholder.storeName')"
          :maxlength="16"
        ></el-input>
      </el-form-item>

      <el-form-item :label="$t('common.memberInformation.managerName')" required>
        <el-row :gutter="16" type="flex">
          <el-col :span="12">
            <el-form-item class="mb-0" prop="lastName">
              <el-input
                id="last-name"
                v-model="form.lastName"
                :placeholder="$t('common.informationPlaceholder.lastNamePlaceholder')"
                :maxlength="7"
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item class="mb-0" prop="firstName">
              <el-input
                id="first-name"
                v-model="form.firstName"
                :placeholder="$t('common.informationPlaceholder.firstNamePlaceholder')"
                :maxlength="7"
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <p class="text-gray-950 text-xs font-normal pt-1">{{ $t('ec-line041.form.nameInputRules') }}</p>
      </el-form-item>

      <el-form-item :label="$t('common.memberInformation.managerKana')" required>
        <el-row :gutter="16" type="flex">
          <el-col :span="12">
            <el-form-item class="mb-0" prop="lastNameKana">
              <el-input
                id="manager-kana"
                v-model="form.lastNameKana"
                :placeholder="$t('common.informationPlaceholder.lastNameKanaPlaceholder')"
                :maxlength="40"
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item class="mb-0" prop="firstNameKana">
              <el-input
                id="manager-kana"
                v-model="form.firstNameKana"
                :placeholder="$t('common.informationPlaceholder.firstNameKanaPlaceholder')"
                :maxlength="40"
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <p class="text-gray-950 text-xs font-normal pt-1">{{ $t('ec-line041.form.nameInputRules') }}</p>
      </el-form-item>

      <el-form-item :label="$t('common.memberInformation.email')" prop="email" required>
        <el-input
          id="email"
          v-model="form.email"
          :placeholder="$t('common.informationPlaceholder.emailPlaceholder')"
          :maxlength="255"
        ></el-input>
      </el-form-item>

      <el-form-item :label="$t('common.memberInformation.emailConfirm')" prop="emailConfirm" required>
        <el-input
          id="email-confirm"
          v-model="form.emailConfirm"
          :placeholder="$t('common.informationPlaceholder.emailPlaceholder')"
          :maxlength="255"
        ></el-input>
      </el-form-item>

      <div class="address-form">
        <p class="store-info">{{ $t('ec-line041.form.storeInfo') }}</p>
        <el-form-item :label="$t('common.memberInformation.postalCode')" required>
          <div class="flex space-x-4 items-center">
            <div class="w-1/4 inline-flex items-center">
              <el-form-item class="mb-0" prop="postalCode01">
                <el-input
                  id="postal-code01"
                  v-model="form.postalCode01"
                  inputmode="numeric"
                  :placeholder="$t('common.informationPlaceholder.postalCodeFirstPlaceholder')"
                  :maxlength="3"
                  @blur="getAddress"
                ></el-input>
              </el-form-item>
              <div class="ml-2 text-center">{{ $t('common.hyphenSeparator') }}</div>
            </div>
            <div class="w-1/4">
              <el-form-item class="mb-0" prop="postalCode02">
                <el-input
                  id="postal-code02"
                  v-model="form.postalCode02"
                  inputmode="numeric"
                  :placeholder="$t('common.informationPlaceholder.postalCodeLastPlaceholder')"
                  :maxlength="4"
                  @blur="getAddress"
                ></el-input>
              </el-form-item>
            </div>
          </div>
          <p class="text-gray-950 text-xs font-normal pt-1">{{ $t('common.messages.postalCodeInputExplanation') }}</p>
        </el-form-item>

        <el-form-item :label="$t('common.memberInformation.prefectures')" prop="prefecture" required>
          <el-select v-model="form.prefecture" id="pref-id" :placeholder="$t('common.pleaseSelect')">
            <el-option v-for="item in prefs" :key="item.id" :value="item.id" :label="item.name"></el-option>
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('common.memberInformation.addr01')" prop="municipality" required>
          <el-input
            id="addr01"
            v-model="form.municipality"
            :placeholder="$t('common.informationPlaceholder.municipalitiesPlaceholder')"
            :maxlength="40"
            v-replace-half-width
          ></el-input>
        </el-form-item>

        <el-form-item :label="$t('common.memberInformation.addr02')" prop="address" required>
          <el-input
            id="addr02"
            v-model="form.address"
            :placeholder="$t('common.informationPlaceholder.addressPlaceholder')"
            :maxlength="40"
            v-replace-half-width
          ></el-input>
        </el-form-item>

        <el-form-item :label="$t('common.memberInformation.addr03')">
          <el-input
            id="addr03"
            v-model="form.buildingApartmentName"
            :placeholder="$t('common.informationPlaceholder.buildingApartmentNamePlaceholder')"
            :maxlength="40"
            v-replace-half-width
          ></el-input>
        </el-form-item>
      </div>

      <el-form-item class="mb-0" :label="$t('common.memberInformation.phoneNumber')" prop="phoneNumber" required>
        <el-input
          id="phone-number"
          v-model="form.phoneNumber"
          inputmode="numeric"
          :placeholder="$t('common.informationPlaceholder.phoneNumberPlaceholder')"
          :maxlength="11"
        ></el-input>
        <p class="text-gray-950 text-xs font-normal pt-1">{{ $t('common.messages.phoneNumberInputRules') }}</p>
      </el-form-item>
    </el-form>
    <div class="flex flex-col items-center justify-center">
      <el-button id="apply-submit" class="submit-btn my-5" @click="onSubmit">
        {{ $t('ec-line041.form.apply') }}
      </el-button>
    </div>
  </div>
</template>

<script>
import { removeIncompatibleCharacters, toHalfWidth, trimCommaForm, trimForm } from '@/utils'
import { PATTERN } from '@/constants'
import { PostCodeJpService } from '@/services'

export default {
  props: {
    value: { type: Object, default: () => {} },
  },

  data() {
    const sameEmailValidator = (rule, value, callback) => {
      if (this.form.email !== value) {
        callback(new Error(this.$t('common.validate.confirm-email-check')))
      }
      callback()
    }

    return {
      rules: {
        lastName: [
          this.Utils.validate.required(this.$t('common.validate.field.managerLastName')),
          this.Utils.validate.shiftjis(this.$t('common.validate.shiftjis')),
        ],
        firstName: [
          this.Utils.validate.required(this.$t('common.validate.field.managerFirstName')),
          this.Utils.validate.shiftjis(this.$t('common.validate.shiftjis')),
        ],
        lastNameKana: [
          this.Utils.validate.required(this.$t('common.validate.field.managerLastNameKana')),
          this.Utils.validate.kanaFormat(this.$t('common.validate.field.managerLastNameKana')),
        ],
        firstNameKana: [
          this.Utils.validate.required(this.$t('common.validate.field.managerFirstNameKana')),
          this.Utils.validate.kanaFormat(this.$t('common.validate.field.managerFirstNameKana')),
        ],
        email: [
          this.Utils.validate.required(this.$t('common.validate.field.email')),
          this.Utils.validate.emailFormat(this.$t('common.validate.field.email')),
        ],
        emailConfirm: [
          this.Utils.validate.required(this.$t('common.validate.field.emailConfirm')),
          this.Utils.validate.emailFormat(this.$t('common.validate.field.emailConfirm')),
          { validator: sameEmailValidator, trigger: ['blur'] },
        ],
        postalCode01: [
          this.Utils.validate.required(this.$t('common.validate.field.postalCode1'), ['blur', 'change']),
          this.Utils.validate.numberFormat(this.$t('common.validate.field.postalCode1')),
        ],
        postalCode02: [
          this.Utils.validate.required(this.$t('common.validate.field.postalCode2'), ['blur', 'change']),
          this.Utils.validate.numberFormat(this.$t('common.validate.field.postalCode2')),
        ],
        prefecture: [this.Utils.validate.requiredSelect(this.$t('common.validate.field.prefecture'))],
        municipality: [this.Utils.validate.required(this.$t('common.validate.field.municipality'))],
        address: [this.Utils.validate.required(this.$t('common.validate.field.address'))],
        phoneNumber: [
          this.Utils.validate.required(this.$t('common.validate.field.phoneNumber')),
          this.Utils.validate.numberFormat(this.$t('common.validate.field.phoneNumber')),
          this.Utils.validate.phoneFormat(this.$t('common.validate.field.phoneNumber')),
        ],
        companyName: [this.Utils.validate.required(this.$t('common.validate.field.companyName'))],
      },
    }
  },

  computed: {
    form: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    prefs() {
      return this.$store.state.master.pref
    },
  },

  methods: {
    async getAddress() {
      this.form = {
        ...this.form,
        postalCode01: removeIncompatibleCharacters(this.form.postalCode01, PATTERN.NON_NUMERIC),
        postalCode02: removeIncompatibleCharacters(this.form.postalCode02, PATTERN.NON_NUMERIC),
      }

      if (!this.form.postalCode01?.trim() || !this.form.postalCode02?.trim()) return

      const res = await PostCodeJpService.getAddress({
        postalCode: this.form.postalCode01 + this.form.postalCode02,
      })

      if (!res) return

      this.form.prefecture = this.prefs.find((item) => item.name === res.prefecture)?.id
      this.form.municipality = toHalfWidth(res.municipality)
      this.form.address = toHalfWidth(res.addr02)
    },

    onSubmit() {
      this.form = trimCommaForm(trimForm(this.form))

      this.$nextTick(() => {
        this.$refs.form.validate(async (isValid) => {
          if (!isValid) {
            return
          }

          this.$emit('handle-submit', this.form)
        })
      })
    },
  },
}
</script>

<style lang="scss">
@import '@/assets/css/components/ec-line041/form-supplier-apply.scss';
</style>
